import { MouseEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormHelperText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// Direct path, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { MESSAGE_CATEGORIES } from './messageCategory';

/*
  The Category the sender picks for a message: joined buttons, one per Category, none picked when
  the window opens, and Send refuses until one is (messageCategorySchema). Each button shows its
  label; the form holds its id, which is what is stored.

  At the top of a single send's compose window, and above a Batch Send's Default message - once for
  the whole batch, since a row that rewords its message is still the same kind of message.
*/
export default function RHFMessageCategory() {
  const { control } = useFormContext();

  return (
    <Controller
      name="category"
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Stack sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary' }}>
              Category
            </Typography>
            <ToggleButtonGroup
              value={value ?? null}
              exclusive
              size="small"
              aria-label="Category"
              // An exclusive group emits null when the picked button is clicked again. Once chosen,
              // a message always has a Category, so that click changes nothing.
              onChange={(_: MouseEvent<HTMLElement>, next: string | null) => next && onChange(next)}
            >
              {/* The group hands its state to each button through context, so a Tooltip between
                  them does not detach the button from the group. */}
              {MESSAGE_CATEGORIES.map(({ id, name, hint }) => (
                <Tooltip key={id} title={hint} arrow>
                  <ToggleButton value={id} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                    {name}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Stack>
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Stack>
      )}
    />
  );
}
